import { Modal } from "../../../components/modals/Modal";
import { GeneralVideoPlayer } from "../../../components/others/GeneralVideoPlayer";
import { ProgressBar } from "../../../components/progressbar/ProgressBar";
import { StackedProgressBar } from "../../../components/progressbar/StackedProgressBar";
import { graphColors } from "../../../services/entity/chart/chart-service";
import React, { useState, useEffect } from "react";
import { CONSTRUCTION_EVENT_TYPES } from "../../../services/gallery/gallery-service";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import BeepOn from "../../../assets/images/beep_on.png";
import BeepOff from "../../../assets/images/beep_off.png";
import { formatSecondsToFullDateTime } from "../../../helpers/date/date-helper";
import { ArchiveBoxIcon, ClockIcon, HeartIcon, PlayIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { patchData } from "../../../services/api/api-tools";
import { useAlert } from "../../../contexts/AlertContext";
import { Button } from "../../../components/buttons/Button";
import { ConfirmModal } from "../../../components/modals/ConfirmModal";
import { ActionButton } from "../../../components/buttons/ActionButton";

export const VideoDetailsModal = ({ selectedRecord, src, isOpen, setIsOpen, refetchRecords, showScores=false }) => {

  const { showAlert } = useAlert();

  const [elapsedTime, setElapsedTime] = useState(0);
  const [data, setData] = useState([]);

  const srcs = Array.isArray(src) ? src : [src];

  const [recordComments, setRecordComments] = useState('');

  const [playerCommand, setPlayerCommand] = useState(null);

  const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState(false);

  useEffect( () => {
    if (!selectedRecord) return;

    setRecordComments(selectedRecord.comments);

    if (!showScores) return;

    const query = `
      import "strings" from(bucket: "engine")
      |> range(start: ${selectedRecord.date}, stop: ${selectedRecord.date + selectedRecord.duration})
      |> filter(fn: (r) => r["_measurement"] == "event_scores")
      |> filter(fn: (r) => r["client_id"] == "${selectedRecord.client_id}")
      |> filter(fn: (r) => r["event_type"] == "${selectedRecord.data?.event_name}")
      |> aggregateWindow(every: 500ms, fn: max, createEmpty: true)
      |> pivot(rowKey: ["_time"], columnKey: ["_field"], valueColumn: "_value")
    `;

    const getData = async () => {
      const newData = await getInfluxdbData(
        'engine',
        'event_scores',
        [],
        [],
        null,
        null,
        null,
        null,
        null,
        [],
        null,
        query,
        false,
        null,
      );
      setData(newData);
    };

    getData();
  }, [selectedRecord]);

  const valueAtTime = (series, t, step = 0.5) => {
    if (!series) return null;

    if (t >= 0 && t / step < series.length)
      return series[Math.floor(t / step)] ?? null;
    else
      return null;
  };

  const handleProgress = (progress) => {
    setElapsedTime(progress.playedSeconds);
  };

  const handleCommentsBlur = async () => {
    try {
      let path = `records/${selectedRecord.id}`;
      let body = { comments: recordComments || null };
      const response = await patchData(path, body);
      if (response) {
        showAlert('success', 'Les commentaires ont été modifiés.');
        refetchRecords();
      } else {
        showAlert('warning', 'Oops! We encountered an issue.');
      }
    } catch (err) {
      console.error('Something bad happened:', err);
    }
  };

  const handleFavoriteClick = async () => {
    try {
      let path = `records/${selectedRecord.id}`;
      let body = { is_favorite: !selectedRecord.is_favorite };
      const response = await patchData(path, body);
      if (response) {
        selectedRecord.is_favorite = !selectedRecord.is_favorite;
        refetchRecords();
      } else {
        showAlert('warning', 'Oops! We encountered an issue.');
      }
    } catch (err) {
      console.error('Something bad happened:', err);
    }
  };

  const handleArchiveClick = async (falsePositive=false) => {
    try {
      let path = `records/${selectedRecord.id}`;
      let body = { is_archived: !selectedRecord.is_archived, data: {...selectedRecord.data, 'false_positive': falsePositive} };
      const response = await patchData(path, body);
      if (response) {
        selectedRecord.is_archived = !selectedRecord.is_archived;
        refetchRecords();
        setIsOpen(false);
      } else {
        showAlert("warning", "Oops! We encountered an issue.");
      }
    } catch (err) {
      console.error("Something bad happened:", err);
    }
  };

  const eventType = selectedRecord?.data ?
    CONSTRUCTION_EVENT_TYPES.find((eventType) => eventType.name === selectedRecord.data?.event_name) : null;

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} size="full">
      <ConfirmModal onConfirm={handleArchiveClick} isOpen={isConfirmArchiveOpen} setIsOpen={setIsConfirmArchiveOpen}
                    title="Confirmer l'archivage" text="Êtes-vous sûr de vouloir archiver ce clip ?" />

      <div className='flex flex-col md:flex-row gap-4 relative h-[90vh] md:h-[80vh]'>
        {
          selectedRecord &&
          <>
            <div className="w-full md:w-9/12 h-4/5 md:h-full flex flex-col md:flex-row gap-4">
              {
                srcs.map((src, srcIndex) => (
                  <GeneralVideoPlayer videoUrl={src} handleOnProgress={handleProgress} startPlaying={false}
                                      startProgress={selectedRecord.data?.max_time || 0} command={playerCommand}
                                      markers={[selectedRecord.data?.max_time].filter((marker) => marker !== null && marker !== undefined)}
                                      key={srcIndex}
                  />
                ))
              }
            </div>
            <div className="flex flex-col gap-4 pt-10 w-full md:w-3/12 md:h-full relative">
              <div className="absolute top-0 left-0 flex mt-1 gap-2">
                <button className="rounded-full inline-block" onClick={handleFavoriteClick} title="Favori">
                  <HeartIcon className={`w-6 h-6 ${selectedRecord.is_favorite ? "text-perception-blue" : "text-perception-gray-100"} hover:text-perception-blue`}/>
                </button>

                <button className="rounded-full inline-block" onClick={() => setIsConfirmArchiveOpen(true)} title="Archiver">
                  <ArchiveBoxIcon className={`w-6 h-6 ${selectedRecord.is_archived ? "text-perception-gray-700" : "text-perception-gray-100"} hover:text-perception-gray-700`}/>
                </button>

                <button className="rounded-full inline-block" onClick={() => handleArchiveClick(true)} title="Ce clip n'est pas pertinent">
                  <XMarkIcon className="w-6 h-6 text-perception-gray-100 hover:text-error"/>
                </button>
              </div>

              {/* Top icons and info */}
              <div className="absolute top-0 right-0 flex items-center">
                <span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block mr-2">
                  #{selectedRecord.id}
                </span>
                <span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block mr-2">
                  {formatSecondsToFullDateTime(selectedRecord.date)}
                </span>
                {/*<span className="rounded-md backdrop-blur-md bg-perception-gray-100 px-2 py-1 text-xs font-medium inline-block ml-4">*/}
                {/*  {CONSTRUCTION_EVENT_TYPES.find((event_type) => event_type.name === selectedRecord.data?.event_name)?.tag_text || 'AUTRES'}*/}
                {/*</span>*/}
                <button className="rounded-full backdrop-blur-md bg-perception-gray-100 px-2 py-2 text-xs font-medium inline-block" onClick={() => setIsOpen(false)}>
                  <XMarkIcon className="w-4 h-4"/>
                </button>
              </div>

              {/* Record scores */}
              {
                showScores && eventType &&
                  <div key={0} className="bg-perception-gray-100 rounded-md p-4">
                    <div className="flex flex-col gap-2 items-start">
                      <h3 className="font-bold">{eventType.display_text}</h3>
                      <ActionButton
                        text="Moment clé" Icon={ClockIcon} size="sm"
                        onClick={() => {
                          const t = selectedRecord?.data?.max_time ?? 0;
                          setPlayerCommand({
                            type: "PAUSE_SEEK_SECONDS",
                            seconds: t,
                            nonce: Date.now(), // force le déclenchement
                          });
                        }}
                      />
                    </div>
                    {
                      eventType.threshold >= 0 &&
                        <StackedProgressBar
                          values={eventType.criteria.map((criterion) => valueAtTime(data, elapsedTime)?.[criterion.name] * 100 || 0)}
                          weights={eventType.criteria.map((criterion) => criterion.weight)}
                          maxValue={valueAtTime(data, elapsedTime)?.final_score * 100}
                          threshold={eventType.threshold}
                          bgColor="white"
                          className="my-4"
                        />
                    }
                    {
                      eventType.criteria.map((criterion, criterionIndex) => (
                        <React.Fragment key={criterionIndex}>
                          <h4 className="text-xs mt-2">{criterion.display_text}</h4>
                          <ProgressBar
                            value={valueAtTime(data, elapsedTime)?.[criterion.name] * 100 || 0}
                            color={graphColors[criterionIndex]}
                            bgColor="bg-white"
                            size="small"
                            className="mt-1"
                          />
                        </React.Fragment>
                      ))
                    }
                  </div>
              }
              <div className="h-full md:h-1/4 flex flex-col gap-4">
                <textarea
                  name="comments"
                  className="w-full bg-perception-gray-100 rounded-md p-4 focus:outline-none resize-none h-full"
                  placeholder="Ajouter des commentaires"
                  value={recordComments}
                  onChange={(e) => setRecordComments(e.target.value)}
                  onBlur={handleCommentsBlur}
                />
                {
                  srcs.length > 1 &&
                  <ActionButton
                    text="Lancer la lecture" size="sm" className="w-full flex justify-center" Icon={PlayIcon}
                    onClick={() => setPlayerCommand({ type: 'PLAY', nonce: Date.now() })}
                  />
                }
              </div>
              {
                showScores &&
                  <div className="flex-grow relative">
                    <img
                      src={valueAtTime(data, elapsedTime)?.final_score * 100 > eventType?.threshold ? BeepOn : BeepOff}
                      alt="beep"
                      className="absolute inset-0 h-full w-full object-contain"
                    />
                  </div>
              }
            </div>
          </>
        }
      </div>
    </Modal>
  );
};
